import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import Navbar from './components/Navbar';
import ErrorBoundary from './components/ErrorBoundary';
import AgentDock from './components/AgentDock';
import { VoiceSessionProvider } from './hooks/VoiceSessionProvider';
import { useApplyTheme } from './theme';
import Landing from './pages/Landing';
import Platform from './pages/Platform';
import ModulePage from './pages/ModulePage';
import StageFixtures from './pages/StageFixtures';
import Store from './pages/Store';
import AppKitDocs from './pages/AppKitDocs';
import Gate from './pages/Gate';
import Watch from './pages/Watch';
import Partners from './pages/Partners';
import Pricing from './pages/Pricing';
import Guide from './pages/Guide';
import IDPPricing from './pages/IDPPricing';
import PricingInternal from './pages/PricingInternal';
import Plan from './pages/Plan';
import Docs from './pages/Docs';
import ConceptPage from './pages/ConceptPage';
import Home from './pages/Home';
import ItemDetail from './pages/ItemDetail';
import Jobs from './pages/Jobs';
import DropUpload from './pages/DropUpload';
import Orgs from './pages/Orgs';
import OrgShell from './pages/org/OrgShell';
import Overview from './pages/org/Overview';
import ReviewModule from './pages/org/ReviewModule';
import AthenaModule from './pages/org/AthenaModule';
import BillingModule from './pages/org/BillingModule';
import EconomicsModule from './pages/org/EconomicsModule';
import AppsModule from './pages/org/AppsModule';
import AppLivePage from './pages/org/AppLivePage';
import OrgLivePage from './pages/org/OrgLivePage';
import AdminApps from './pages/AdminApps';
import AdminFleetLive from './pages/AdminFleetLive';
import AdminBrain from './pages/AdminBrain';
import ContentPlanningModule from './pages/org/ContentPlanningModule';
import AdminEngineering from './pages/AdminEngineering';
import DevStudioModule from './pages/org/DevStudioModule';
import AppBuilderModule from './pages/org/AppBuilderModule';
import KnowledgeModule from './pages/org/KnowledgeModule';
import DataModule from './pages/org/DataModule';
import WorkflowsModule from './pages/org/WorkflowsModule';
import IntegrationsModule from './pages/org/IntegrationsModule';
import BlueprintModule from './pages/org/BlueprintModule';
import QAModule from './pages/org/QAModule';
import DeploymentsModule from './pages/org/DeploymentsModule';
import NovaModule from './pages/org/NovaModule';
import AdministrationModule from './pages/org/AdministrationModule';
import SolutionsPage from './pages/org/SolutionsPage';
import ProjectShell, {
  ProjectOverview, ProjectNQube, ProjectCloudOps, ProjectDevStudio, ProjectListen,
} from './pages/org/ProjectShell';
import WorkspaceDetail from './pages/WorkspaceDetail';
import PipelineEditor from './pages/PipelineEditor';
import PipelineRun from './pages/PipelineRun';
import PipelineIde from './pages/PipelineIde';
import Login from './components/Login';
import AuthShell from './components/AuthShell';
import PlatformNav from './components/PlatformNav';
import Invites from './pages/Invites';
import Welcome from './pages/Welcome';
import InstanceSso from './pages/InstanceSso';
import { Physical, Privacy, Terms, Eula, Security } from './pages/SitePages';
import { ClickwrapGate } from './components/Clickwrap';
import Support from './pages/Support';
import SupportQueue from './pages/SupportQueue';
import MyBugs from './pages/MyBugs';
import AdminBugs from './pages/AdminBugs';
import AdminBugDetail from './pages/AdminBugDetail';
import Features from './pages/Features';
import AdminFeatures from './pages/AdminFeatures';
import AdminFeatureDetail from './pages/AdminFeatureDetail';
import AdminHelpdesk from './pages/AdminHelpdesk';
import BugReportDialog from './components/BugReportDialog';
import StartCheckout from './pages/StartCheckout';
import LikenessPage from './pages/Likeness';
import { useAuth, signOut, isVerified } from './AuthContext';
import VerifyEmail from './components/VerifyEmail';
import AuthAction from './pages/AuthAction';
import OrgStart from './components/OrgStart';
import { useMyOrgs } from './data/entities';
import { useDoc } from './data/firestore';
import registry from './data/entityRegistry.gen.json';
import { moduleBySlug } from './data/modules';

// Full-page 3D rooms carry the three.js stack: one lazy chunk each.
const AppRoom = lazy(() => import('./pages/room/AppRoom'));
const FleetRoom = lazy(() => import('./pages/room/FleetRoom'));
const DemoRoom = lazy(() => import('./pages/room/RoomFixtures').then((m) => ({ default: m.DemoRoom })));
const PortfolioRoom = lazy(() => import('./pages/room/PortfolioRoom'));
const roomOf = (el) => <RequireAuth><Suspense fallback={<div className="room"><div className="room-state">loading the room…</div></div>}>{el}</Suspense></RequireAuth>;

const PAGE_TITLES = [
  ['/guide', 'The Onboarding Guidebook · nventr', 'Map, then optimize: map your systems, data, processes and people; let nventr propose the optimizations; build them; track the savings.'],
  ['/start', 'Start · nventr', 'Create an account: Platform Free with no card, or pay for Platform Start or App Kit. The order stays on the page until Stripe Checkout.'],
  ['/pricing', 'Pricing · nventr', 'Platform or App Kit. List prices for gated workflows and a dedicated instance.'],
  ['/app-kit', 'App Kit · nventr', 'Rent the app. Dev Console is how you change it. The foundation under that loop carries large files and heavy jobs, checks health on every deploy, and updates without touching your layer.'],
  ['/store', 'Hardware · nventr', 'Appliances and nodes that ship with nventr OS. The business is the runtime, not the box.'],
  ['/partners', 'Partners · nventr', 'Telcos and ISPs own the metal. nventr licenses the OS.'],
  ['/platform', 'Platform · nventr', 'nQube designs the workflow. agentOS commits it. Dev Console requires App Kit.'],
  ['/physical', 'GridOS · nventr', 'Control plane for warehouses, fleets, and operators. Separate from Platform and App Kit.'],
  ['/privacy', 'Privacy · nventr', 'What Nventr AI, LLC collects, and what it does not sell.'],
  ['/terms', 'Terms · nventr', 'The contract for a nventr.io account, Platform, and App Kit.'],
  ['/eula', 'End user agreement · nventr', 'The license for the person using the software.'],
  ['/security', 'Security · nventr', 'A person or a keyword filter gates every write. The runtime commits. Credentials stay out of the model.'],
  ['/support', 'Support · nventr', 'Send a request. We answer from what nventr knows, and the right person on the team is notified.'],
  ['/contact', 'Support · nventr', 'Send a request. We answer from what nventr knows, and the right person on the team is notified.'],
  ['/bugs', 'Your bug reports · nventr', 'What you reported and where each fix is on the way to production.'],
  ['/admin/bugs', 'Bug reports · nventr', 'Review reported bugs, propose a fix, build to UAT, approve and promote.'],
  ['/features', 'Feature board · nventr', 'What members are asking for. Vote, request a feature, follow it to production.'],
  ['/admin/features', 'Feature requests · nventr', 'Triage requests, draft a plan with the agent, approve it into a PR, build to UAT, promote.'],
  ['/admin/helpdesk', 'Help desk · nventr', 'What the autopilot did and what waits for your approval — the same one-click decisions the digest mails carry.'],
  ['/gate', 'What a gate actually is · nventr', 'Approval in front of a tool vs a model that holds no write-capable tool. How a step picks its model: four tiers, verified-failure escalation, caps.'],
  ['/watch', 'The bridge, and one brain · nventr', 'A film with sound. nventr is the bridge to the systems you use and the apps you build. You draw the work, a swarm plans it, and App Kit is the instance. Then the numbers measured on our own fleet.'],
  ['/', 'nventr — The bridge to the systems you use, and the apps you build', 'Agents propose. A person or a rule says yes. Only the runtime writes. One brain for the organization, a team, an app, or the instance.'],
];

function usePageTitle(pathname) {
  useEffect(() => {
    const mod = pathname.startsWith('/platform/') ? moduleBySlug(pathname.split('/')[2]) : null;
    const hit = PAGE_TITLES.find(([prefix]) => (prefix === '/' ? pathname === '/' : pathname.startsWith(prefix)));
    const title = mod ? `${mod.name} · nventr` : (hit ? hit[1] : 'nventr');
    const description = mod ? mod.card : (hit ? hit[2] : '');
    document.title = title;
    const meta = document.querySelector('meta[name="description"]');
    if (meta && description) meta.setAttribute('content', description);
    const canonicalPath = pathname === '/' ? '/' : pathname;
    const href = `https://nventr.io${canonicalPath}`;
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', href);
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', href);
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', title);
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc && description) ogDesc.setAttribute('content', description);
  }, [pathname]);
}

// app.nventr.io is the platform app: no marketing routes, signing in drops
// you straight into the console. nventr.io keeps the public site (with the
// same console reachable under /orgs for convenience).
const IS_APP_HOST = /^app(-uat)?\./.test(window.location.hostname);

// App shell pattern: public routes render freely, admin routes are wrapped in
// RequireAuth, and every route body sits inside an ErrorBoundary keyed by
// pathname so one page crashing never takes down navigation.
// Where a signed-in member lands after /login: the `next` deep link when it
// is a same-origin path, else the console.
function afterLogin(search) {
  const next = new URLSearchParams(search).get('next') || '';
  if (next.startsWith('/') && !next.startsWith('//')) return next;
  return '/console'; // last-used org (ConsoleHome); /orgs keeps the list
}

// /admin/films/<slug>/<view> → the Content planning module of nventr's own
// org (config/app content.ownOrgId; plan ids are the old film slugs). The
// old 'plan' view is the Stages tab now.
function FilmsRedirect() {
  const { slug, view } = useParams();
  const { doc: config, error } = useDoc('config/app');
  if (config === undefined && !error) return null;
  const org = config?.content?.ownOrgId || 'team';
  const v = view && view !== 'plan' ? `/${view}` : '';
  return <Navigate to={`/orgs/${org}/content-planning${slug ? `/${slug}${v}` : ''}`} replace />;
}

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  // Gate, in the order the rules apply it: signed in → verified email →
  // a seat (founder allowlist or an accessGrants doc, written by org
  // creation, invites and approved join requests). The rules are the real
  // boundary; these screens are honest UX for each step.
  const allowlist = registry.accessAllowlist;
  const member = !!user && !user.isAnonymous;
  const listed = member && (!allowlist || allowlist.includes(user.email));
  // reload()/getIdToken(true) mutate the cached user in place without a new
  // onAuthStateChanged — this tick forces the re-read once a link is clicked.
  const [, bump] = useState(0);
  const { doc: grant } = useDoc(
    member && !listed ? `accessGrants/${(user.email || '').toLowerCase()}` : null
  );
  if (loading) return <div className="cs-auth-wrap">Loading…</div>;
  // Anonymous sessions (created by the public chat agent) don't count. Send
  // signed-out deep links to /login with the target preserved, so the page
  // reads as "sign in" (not a marketing page with a stray form) and sign-in
  // returns them to what they asked for.
  if (!member) {
    const next = location.pathname + location.search;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  if (!isVerified(user)) return <VerifyEmail user={user} onVerified={() => bump((n) => n + 1)} />;
  if (!listed) {
    if (grant === undefined) return <div className="cs-auth-wrap">Loading…</div>;
    if (!grant) {
      // No seat yet. This is not a dead end: a verified person either
      // creates their organization (consumer or unclaimed company domain)
      // or asks to join the one that has proven their domain — the grant
      // appears when either lands, and this re-renders into the console.
      return (
        <ClickwrapGate>
          <div className="cs-auth-wrap">
            <div style={{ maxWidth: 600, width: '100%' }}>
              <h2>Welcome, {user.email}</h2>
              <p className="cs-help">
                You are signed in with a verified email but not part of an organization yet.
                If a colleague invited you, the invite is on its way to this address and lands you in automatically.
              </p>
              <OrgStart compact />
              <p style={{ marginTop: 18 }}>
                <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => signOut()} data-agent-noclick>Sign out</button>
              </p>
            </div>
          </div>
        </ClickwrapGate>
      );
    }
  }
  return <ClickwrapGate>{children}</ClickwrapGate>;
}

// Signing in drops you into your default organization (last used, else the
// first one) — the org list stays reachable at /orgs and via the switcher.
function ConsoleHome() {
  const orgs = useMyOrgs();
  if (orgs === null) return <div className="cs-auth-wrap">Loading…</div>;
  if (!orgs.length) return <Orgs />;
  let last = null;
  try { last = localStorage.getItem('nv.lastOrgId'); } catch { /* ignore */ }
  const target = orgs.find((o) => o.id === last) || orgs[0];
  return <Navigate to={`/orgs/${target.id}`} replace />;
}

export default function App() {
  const { user } = useAuth();
  const member = user && !user.isAnonymous;
  const location = useLocation();
  usePageTitle(location.pathname);
  // Light/dark preference applies to the platform app (app host, or the
  // console / admin / jobs routes on the public host); marketing stays dark.
  useApplyTheme(IS_APP_HOST || /^\/(orgs|admin|jobs)(\/|$)/.test(location.pathname));

  // Route changes keep the previous scroll offset, and a sticky nav covers
  // a hash target that lands flush with the viewport. Hash wins; a plan
  // query lands on the checkout note; everything else starts at the top.
  useEffect(() => {
    const hashId = location.hash ? decodeURIComponent(location.hash.slice(1)) : '';
    const plan = new URLSearchParams(location.search).get('plan');
    const targetId = hashId || (plan ? 'checkout-intent' : '');
    const go = () => {
      const el = targetId ? document.getElementById(targetId) : null;
      if (el) el.scrollIntoView({ block: 'start' });
      else window.scrollTo(0, 0);
    };
    go();
    const t = window.setTimeout(go, 0);
    return () => window.clearTimeout(t);
  }, [location.pathname, location.hash, location.search]);

  // Public guest-upload route is reachable without signing in — checked before
  // anything else (no navbar, no auth) so contributors with a link can upload.
  const isDropRoute = /^\/u\//.test(window.location.pathname);
  if (isDropRoute) {
    return (
      <ErrorBoundary resetKey={location.pathname}>
        <Routes>
          <Route path="/u/:token" element={<DropUpload />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ErrorBoundary>
    );
  }

  // Inside an org console the copilot dock (OrgShell → CopilotPanel) is the
  // ONE floating agent — voice folded in — so the standalone voice widget
  // stays off those routes (no two launchers in the corner).
  const inOrgConsole = /^\/orgs\/./.test(location.pathname);

  // Org console: module sidebar shell (nQube / Agent IO / Compute / Athena)
  // with module pages in the outlet. Shared verbatim by both hosts.
  const orgRoutes = (
    <Route path="/orgs/:orgId" element={<RequireAuth><OrgShell /></RequireAuth>}>
      <Route index element={<Overview />} />
      <Route path="solutions" element={<SolutionsPage />} />
      {/* The deck's eleven modules (decision deck-ia-console). */}
      <Route path="blueprint" element={<BlueprintModule />} />
      <Route path="builder" element={<AppBuilderModule />} />
      <Route path="knowledge" element={<KnowledgeModule />} />
      <Route path="content-planning" element={<ContentPlanningModule />} />
      <Route path="content-planning/:planId/:view?" element={<ContentPlanningModule />} />
      <Route path="data" element={<DataModule />} />
      <Route path="workflows" element={<WorkflowsModule />} />
      <Route path="integrations" element={<IntegrationsModule />} />
      <Route path="human-review" element={<ReviewModule />} />
      <Route path="qa" element={<QAModule />} />
      <Route path="deployments" element={<DeploymentsModule />} />
      <Route path="nova" element={<NovaModule />} />
      <Route path="analytics" element={<AthenaModule />} />
      <Route path="administration" element={<AdministrationModule />} />
      <Route path="settings" element={<BillingModule />} />
      <Route path="economics" element={<EconomicsModule />} />
      {/* Legacy module paths redirect into the new IA (bookmarks, copilot
          navigate_app targets, older emails). Product names live on inside. */}
      <Route path="nqube" element={<Navigate to="../workflows" replace />} />
      <Route path="agents" element={<Navigate to="../workflows?tab=agents" replace />} />
      <Route path="listen" element={<Navigate to="../data?tab=datasets" replace />} />
      <Route path="review" element={<Navigate to="../human-review" replace />} />
      <Route path="athena" element={<Navigate to="../analytics" replace />} />
      <Route path="members" element={<Navigate to="../administration" replace />} />
      <Route path="billing" element={<Navigate to="../settings" replace />} />
      <Route path="compute" element={<Navigate to="../deployments?tab=compute" replace />} />
      <Route path="cloudops" element={<Navigate to="../deployments?tab=compute" replace />} />
      <Route path="apps" element={<Navigate to="../deployments?tab=instances" replace />} />
      <Route path="live/:aspect" element={<OrgLivePage />} />
      <Route path="apps/:appId/live/:aspect" element={<AppLivePage />} />
      {/* Still-rendered legacy surfaces: Dev Studio (linked from App Builder's
          Build step) and the per-instance detail page (deep links everywhere). */}
      <Route path="devstudio" element={<DevStudioModule />} />
      <Route path="apps/:appId" element={<AppsModule />} />
      {/* Project-scoped module views: the sidebar's active project. */}
      <Route path="p/:workspaceId/:projectId" element={<ProjectShell />}>
        <Route index element={<ProjectOverview />} />
        <Route path="nqube" element={<ProjectNQube />} />
        <Route path="cloudops" element={<ProjectCloudOps />} />
        <Route path="devstudio" element={<ProjectDevStudio />} />
        <Route path="listen" element={<ProjectListen />} />
      </Route>
      <Route path="ws/:workspaceId" element={<WorkspaceDetail />} />
      <Route path="ws/:workspaceId/pipelines/:pipelineId" element={<PipelineEditor />} />
      <Route path="ws/:workspaceId/runs/:jobId" element={<PipelineRun />} />
      <Route path="ws/:workspaceId/pipelines/:pipelineId/ide" element={<PipelineIde />} />
    </Route>
  );

  // Immersive rooms: the command stages as full-page worlds (no org shell —
  // the room is the whole viewport). Static clients bounce back to the stage.
  const roomRoutes = (
    <>
      <Route path="/orgs/:orgId/apps/:appId/room" element={roomOf(<AppRoom />)} />
      <Route path="/orgs/:orgId/room" element={roomOf(<FleetRoom mode="org" />)} />
      <Route path="/admin/fleet/room" element={roomOf(<FleetRoom mode="admin" />)} />
    </>
  );

  // ---- Platform app (app.nventr.io) ----------------------------------------
  if (IS_APP_HOST) {
    return (
      <VoiceSessionProvider>
        <PlatformNav />
        <ErrorBoundary resetKey={location.pathname}>
          <Routes>
            {/* Console home forwards to your default org; /orgs keeps the
                full list (and invite links /orgs?invite=… on both hosts). */}
            <Route path="/" element={<RequireAuth><ConsoleHome /></RequireAuth>} />
            <Route path="/console" element={<RequireAuth><ConsoleHome /></RequireAuth>} />
            <Route
              path="/login"
              element={member ? <Navigate to={afterLogin(location.search)} replace /> : <AuthShell><Login /></AuthShell>}
            />
            {/* Links in our verification / reset emails land here. */}
            <Route path="/auth/action" element={<AuthAction />} />
            <Route path="/orgs" element={<RequireAuth><Orgs /></RequireAuth>} />
            {roomRoutes}
            {orgRoutes}
            {/* Operator-only fleet admin (backend 403s non-operators). */}
            <Route path="/support" element={<Support />} />
            <Route path="/bugs" element={<RequireAuth><MyBugs /></RequireAuth>} />
            <Route path="/features" element={<RequireAuth><Features /></RequireAuth>} />
            <Route path="/admin/support" element={<RequireAuth><SupportQueue /></RequireAuth>} />
            <Route path="/admin/bugs" element={<RequireAuth><AdminBugs /></RequireAuth>} />
            <Route path="/admin/bugs/:id" element={<RequireAuth><AdminBugDetail /></RequireAuth>} />
            <Route path="/admin/features" element={<RequireAuth><AdminFeatures /></RequireAuth>} />
          <Route path="/admin/helpdesk" element={<RequireAuth><AdminHelpdesk /></RequireAuth>} />
            <Route path="/admin/features/:id" element={<RequireAuth><AdminFeatureDetail /></RequireAuth>} />
            <Route path="/admin/apps" element={<RequireAuth><AdminApps /></RequireAuth>} />
            <Route path="/admin/fleet/:aspect" element={<RequireAuth><AdminFleetLive /></RequireAuth>} />
            {/* Super-admin knowledge base (Firestore rules: founders only). */}
            <Route path="/admin/brain" element={<RequireAuth><AdminBrain /></RequireAuth>} />
            <Route path="/admin/brain/:section/:slug?" element={<RequireAuth><AdminBrain /></RequireAuth>} />
            {/* Films moved into the Content planning module of nventr's own org. */}
            <Route path="/admin/films" element={<RequireAuth><FilmsRedirect /></RequireAuth>} />
            <Route path="/admin/films/:slug/:view?" element={<RequireAuth><FilmsRedirect /></RequireAuth>} />
          <Route path="/admin/engineering" element={<RequireAuth><AdminEngineering /></RequireAuth>} />
          <Route path="/admin/engineering/:tab/:slug?" element={<RequireAuth><AdminEngineering /></RequireAuth>} />
            <Route path="/admin/engineering" element={<RequireAuth><AdminEngineering /></RequireAuth>} />
            <Route path="/admin/engineering/:tab/:slug?" element={<RequireAuth><AdminEngineering /></RequireAuth>} />
            <Route path="/jobs" element={<RequireAuth><Jobs /></RequireAuth>} />
            <Route path="/likeness" element={<RequireAuth><LikenessPage /></RequireAuth>} />
            {/* Access-invite flow works on both hosts so links keep their origin. */}
            <Route path="/welcome" element={<Welcome />} />
            {/* "Sign in with nventr.io" from a hosted instance's login page. */}
            <Route path="/sso" element={<RequireAuth><InstanceSso /></RequireAuth>} />
            <Route path="/invites" element={<RequireAuth><Invites /></RequireAuth>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </ErrorBoundary>
        {/* Members-only agent dock (voice + chat); the org console mounts
            its own copilot dock instead. */}
        {member && !inOrgConsole && location.pathname !== '/likeness' && <AgentDock voiceEnabled member />}
        <BugReportDialog />
      </VoiceSessionProvider>
    );
  }

  // ---- Public site (nventr.io) ----------------------------------------------
  return (
    <VoiceSessionProvider>
      <Navbar />
      <ErrorBoundary resetKey={location.pathname}>
        <Routes>
          {/* ---- Public ---- */}
          <Route path="/" element={<Landing />} />
          <Route path="/platform" element={<Platform />} />
          <Route path="/_stages" element={<StageFixtures />} />
          {/* The homepage's Command View as a full room on simulated data — "enter the room ↗" on the Operate panel. */}
          <Route path="/demo/room" element={<Suspense fallback={null}><DemoRoom /></Suspense>} />
          <Route path="/platform/app-kit" element={<Navigate to="/app-kit" replace />} />
          <Route path="/platform/cloudops" element={<Navigate to="/platform/devops" replace />} />
          <Route path="/platform/:slug" element={<ModulePage />} />
          <Route path="/hardware" element={<Navigate to="/store" replace />} />
          <Route path="/store" element={<Store />} />
          {/* Public App Kit explainer: rented-kit model, deployment options,
              phone-home transparency. Linked from Landing #hosted-apps. */}
          <Route path="/app-kit" element={<AppKitDocs />} />
          <Route path="/partners" element={<Partners />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/start" element={<StartCheckout />} />
          <Route path="/guide" element={<Guide />} />
          <Route path="/physical" element={<Physical />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/eula" element={<Eula />} />
          <Route path="/security" element={<Security />} />
          <Route path="/contact" element={<Support />} />
          <Route path="/support" element={<Support />} />
          {/* What a gate actually is + model routing — the #proofs targets. */}
          <Route path="/gate" element={<Gate />} />
          <Route path="/watch" element={<Watch />} />
          {/* Signing in means "take me to the platform" — land on the console. */}
          <Route
            path="/login"
            element={member ? <Navigate to={afterLogin(location.search)} replace /> : <AuthShell><Login /></AuthShell>}
          />
          {/* Links in our verification / reset emails land here. */}
          <Route path="/auth/action" element={<AuthAction />} />

          {/* Invitees land here with their token; email pre-filled, set a
              password, and the grant admits them. */}
          <Route path="/welcome" element={<Welcome />} />
          {/* "Sign in with nventr.io" from a hosted instance's login page. */}
          <Route path="/sso" element={<RequireAuth><InstanceSso /></RequireAuth>} />

          {/* ---- Login-protected (sign-in required) ---- */}
          <Route path="/plan" element={<RequireAuth><Plan /></RequireAuth>} />
          <Route path="/docs" element={<RequireAuth><Docs /></RequireAuth>} />
          {/* Gated concept pages — allowlist like /docs; never in public nav. */}
          <Route path="/concepts/:page" element={<RequireAuth><ConceptPage /></RequireAuth>} />
          {/* The portfolio book's simulated fleet as a walkable room. */}
          <Route path="/concepts/portfolio/room" element={<RequireAuth><Suspense fallback={null}><PortfolioRoom /></Suspense></RequireAuth>} />
          {/* Private enterprise rate card — allowlist-gated like /plan. */}
          <Route path="/idp-pricing" element={<RequireAuth><IDPPricing /></RequireAuth>} />
          {/* Full-spec internal pricing — the NDA companion to /pricing. */}
          <Route path="/pricing-internal" element={<RequireAuth><PricingInternal /></RequireAuth>} />
          {/* Founder-only access-invite manager. */}
          <Route path="/invites" element={<RequireAuth><Invites /></RequireAuth>} />
          <Route path="/items" element={<RequireAuth><Home /></RequireAuth>} />
          <Route path="/items/:id" element={<RequireAuth><ItemDetail /></RequireAuth>} />
          <Route path="/jobs" element={<RequireAuth><Jobs /></RequireAuth>} />
          <Route path="/bugs" element={<RequireAuth><MyBugs /></RequireAuth>} />
          <Route path="/features" element={<RequireAuth><Features /></RequireAuth>} />
          <Route path="/likeness" element={<RequireAuth><LikenessPage /></RequireAuth>} />

          {/* ---- Platform console (tenancy spine: org → workspace → project) ---- */}
          {/* /console = your last-used org on the public host too, matching app.nventr.io. */}
          <Route path="/console" element={<RequireAuth><ConsoleHome /></RequireAuth>} />
          <Route path="/orgs" element={<RequireAuth><Orgs /></RequireAuth>} />
          {roomRoutes}
          {orgRoutes}

          {/* Super-admin surfaces, reachable from the public host too. */}
          <Route path="/admin/support" element={<RequireAuth><SupportQueue /></RequireAuth>} />
          <Route path="/admin/bugs" element={<RequireAuth><AdminBugs /></RequireAuth>} />
          <Route path="/admin/bugs/:id" element={<RequireAuth><AdminBugDetail /></RequireAuth>} />
          <Route path="/admin/features" element={<RequireAuth><AdminFeatures /></RequireAuth>} />
            <Route path="/admin/helpdesk" element={<RequireAuth><AdminHelpdesk /></RequireAuth>} />
          <Route path="/admin/features/:id" element={<RequireAuth><AdminFeatureDetail /></RequireAuth>} />
          <Route path="/admin/apps" element={<RequireAuth><AdminApps /></RequireAuth>} />
          <Route path="/admin/fleet/:aspect" element={<RequireAuth><AdminFleetLive /></RequireAuth>} />
          <Route path="/admin/brain" element={<RequireAuth><AdminBrain /></RequireAuth>} />
          <Route path="/admin/brain/:section/:slug?" element={<RequireAuth><AdminBrain /></RequireAuth>} />
          <Route path="/admin/films" element={<RequireAuth><FilmsRedirect /></RequireAuth>} />
          <Route path="/admin/films/:slug/:view?" element={<RequireAuth><FilmsRedirect /></RequireAuth>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ErrorBoundary>

      {/* The agent dock: voice + text for everyone on the public site.
          Visitors are signed in anonymously; the backend keeps them on
          public tools (ask, navigate, scroll) and rate-limits voice.
          Inside the org console the copilot dock takes over. */}
      {!inOrgConsole && location.pathname !== '/likeness' && <AgentDock voiceEnabled member={!!member} />}
      <BugReportDialog />
    </VoiceSessionProvider>
  );
}
